/**
 * Map Grafana panel types to our internal types
 */
const mapPanelType = (grafanaType) => {
  const typeMap = {
    'timeseries': 'timeseries',
    'graph': 'timeseries',
    'gauge': 'gauge',
    'stat': 'stat',
    'piechart': 'pie',
    'table': 'table',
    'logs': 'logs',
    'row': 'row'
  };
  return typeMap[grafanaType] || 'timeseries';
};

/**
 * Parse Grafana time range (e.g., "now-6h" to dayjs objects)
 */
const parseGrafanaTime = (timeConfig) => {
  // For now, return default. We'll implement full parser later
  return {
    from: timeConfig?.from || 'now-6h',
    to: timeConfig?.to || 'now'
  };
};

/**
 * Import Grafana dashboard JSON
 */
export const importGrafanaDashboard = (grafanaJSON) => {
  try {
    const dashboard = grafanaJSON.dashboard || grafanaJSON;
    
    if (!dashboard.panels) {
      throw new Error('Invalid Grafana JSON: missing panels array');
    }

    // Convert panels
    const panels = dashboard.panels
      .filter(panel => panel.type !== 'row')
      .map(panel => ({
        id: panel.id,
        type: mapPanelType(panel.type),
        title: panel.title || 'Untitled Panel',
        description: panel.description || '',
        gridPos: panel.gridPos || { x: 0, y: 0, w: 12, h: 8 },
        targets: panel.targets || [],
        datasource: panel.datasource || null,
        fieldConfig: panel.fieldConfig || {
          defaults: {},
          overrides: []
        },
        options: panel.options || {},
        data: [],
        loading: false,
        error: null
      }));

    // ==========================================
    // NEW: Process template variables
    // ==========================================
    const templating = dashboard.templating || { list: [] };
    
    // Initialize variable values from current values
    const initialVariables = {};
    templating.list.forEach(variable => {
      // Try multiple places to find current value
      const currentValue = 
        variable.current?.value || 
        variable.current?.text || 
        (Array.isArray(variable.options) && variable.options[0]?.value) ||
        '';
      
      initialVariables[variable.name] = currentValue;
      console.log(`Initializing variable ${variable.name} = ${currentValue}`);
    });

    const importedDashboard = {
      uid: dashboard.uid || `imported_${Date.now()}`,
      title: dashboard.title || 'Imported Dashboard',
      description: dashboard.description || '',
      tags: dashboard.tags || [],
      time: parseGrafanaTime(dashboard.time),
      timezone: dashboard.timezone || 'browser',
      refresh: dashboard.refresh || '',
      panels: panels,
      templating: templating,
      variables: initialVariables,
      schemaVersion: dashboard.schemaVersion || 1,
      version: dashboard.version || 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return {
      success: true,
      dashboard: importedDashboard,
      panelCount: panels.length,
      variableCount: templating.list.length // ← Report variable count
    };
    
  } catch (error) {
    console.error('Error importing Grafana dashboard:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Export to Grafana-compatible JSON
 */
export const exportToGrafana = (dashboard) => {
  try {
    const grafanaJSON = {
      dashboard: {
        uid: dashboard.uid,
        title: dashboard.title,
        description: dashboard.description,
        tags: dashboard.tags,
        timezone: dashboard.timezone,
        time: dashboard.time,
        refresh: dashboard.refresh || '',
        
        // Panels
        panels: dashboard.panels.map(panel => ({
          id: panel.id,
          type: panel.type,
          title: panel.title,
          description: panel.description,
          gridPos: panel.gridPos,
          targets: panel.targets,
          datasource: panel.datasource,
          fieldConfig: panel.fieldConfig,
          options: panel.options
        })),
        
        // Template variables
        templating: dashboard.templating,
        
        // Metadata
        schemaVersion: dashboard.schemaVersion || 39,
        version: dashboard.version || 1
      }
    };

    return {
      success: true,
      json: grafanaJSON
    };
    
  } catch (error) {
    console.error('Error exporting dashboard:', error);
    return {
      success: false,
      error: error.message
    };
  }
};